import { queryOptions, useQuery } from '@tanstack/react-query';

import { getEventOnceApi, getEventsList } from '@/entities/event/api';
import type { EventDetailItem, EventTab } from '@/entities/event/config';
import { SETTINGS } from '@/shared/config';

import { adaptEventDetailItem } from './adapters/eventDetailAdapter';
import { adaptEventListItem } from './adapters/eventListAdapter';

export const eventsQueryKeys = {
  all: ['events'] as const,
  list: (filter: EventTab, limit: number, offset: number) =>
    [...eventsQueryKeys.all, 'list', filter, limit, offset] as const,
  pageOnce: (slug: string) =>
    [...eventsQueryKeys.all, 'event-once', { slug }] as const,
};

export interface UseEventsListParams {
  filter: EventTab;
  limit?: number;
  offset: number;
}

export const eventsListQueryOptions = ({
  filter,
  limit = SETTINGS.limit,
  offset,
}: UseEventsListParams) =>
  queryOptions({
    queryKey: eventsQueryKeys.list(filter, limit, offset),
    queryFn: async () => {
      const data = await getEventsList({ filter, limit, offset });
      return {
        items: data.items.map(adaptEventListItem),
        pagination: data.pagination,
      };
    },
  });

export const useEventsList = (params: UseEventsListParams) =>
  useQuery({
    ...eventsListQueryOptions(params),
    placeholderData: (previousData) => previousData,
  });

export async function fetchEventOnce(
  slug: string,
): Promise<EventDetailItem | null> {
  const dto = await getEventOnceApi(slug);
  return dto ? adaptEventDetailItem(dto) : null;
}

export const useEventOnceData = (slug: string) => {
  return useQuery({
    queryKey: eventsQueryKeys.pageOnce(slug),
    queryFn: () => fetchEventOnce(slug),
    placeholderData: (previousData) => previousData,
  });
};
